import { splitMessageAttachments } from './file-chips';
import { stripDirectivesForLabel } from './helpers/skill-directive';
import type { AgentConversationSummary } from '@/services/agents/config';

/**
 * The stored preview is the raw user turn, so it still carries the custom
 * instructions / skill directive blocks and the attachment manifest the
 * composer appends.
 */
export function conversationPreview(
  conversation: Pick<AgentConversationSummary, 'preview'>,
): string {
  if (!conversation.preview) return '';
  const text = stripDirectivesForLabel(conversation.preview);
  return splitMessageAttachments(text).text.trim();
}

export function conversationLabel(
  conversation: Pick<AgentConversationSummary, 'title' | 'preview'>,
): string {
  // Server-generated titles summarize the raw first message, so a directive
  // block (possibly truncated mid-block) can leak into them too.
  const title = conversation.title
    ? stripDirectivesForLabel(conversation.title)
    : '';
  return title || conversationPreview(conversation) || '(untitled)';
}

/**
 * The timestamp a conversation is dated by: when it was last replied to, or
 * else when it was started. The single fallback every recency-ordered view
 * (the chats list, the chat card, the "continue your conversation" pick)
 * reads from, so a future timestamp field only needs to be added here.
 */
export function conversationRecencyStamp(
  conversation: Pick<
    AgentConversationSummary,
    'last_message_at' | 'created_at'
  >,
): string | undefined {
  return conversation.last_message_at ?? conversation.created_at;
}

/**
 * The thread "Continue your conversation" should land in.
 *
 * The list endpoint does not promise an order, so pick explicitly rather than
 * trusting index 0. A conversation that has never been replied to is dated by
 * when it was created; one dated by nothing at all sorts last, since there is
 * no evidence it is recent.
 */
export function latestConversation(
  conversations: AgentConversationSummary[],
): AgentConversationSummary | null {
  let latest: AgentConversationSummary | null = null;
  let latestAt = -Infinity;

  for (const conversation of conversations) {
    const stamp = conversationRecencyStamp(conversation);
    const at = stamp ? Date.parse(stamp) : NaN;
    // Strictly greater keeps the endpoint's own order for ties.
    if (Number.isFinite(at) && at > latestAt) {
      latest = conversation;
      latestAt = at;
    }
  }

  return latest ?? conversations[0] ?? null;
}
